import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { DocService } from '@affine/core/modules/doc';
import { FolderShareModal } from '@affine/core/modules/folder-share/views/folder-share-modal';
import {
  getObjectPermissionAreaBlockedNotice,
  objectPermissionRoleLabel,
  useObjectPermissionOnlineOnlyState,
} from '@affine/core/modules/permissions/utils/object-permission-ui';
import * as styles from '@affine/core/modules/permissions/views/permission-access-list.css';
import {
  PermissionGroupRow,
  PermissionMemberRow,
} from '@affine/core/modules/permissions/views/permission-invite-picker';
import { PermissionReviewList } from '@affine/core/modules/permissions/views/permission-review-list';
import * as reviewStyles from '@affine/core/modules/permissions/views/permission-review-list.css';
import { PERMISSION_REVIEW_PAGE_SIZE } from '@affine/core/modules/permissions/views/use-doc-permission-review';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  type GetDocInheritedAccessQuery,
  getDocInheritedAccessQuery,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useEffect, useState } from 'react';

import * as panelStyles from './doc-access-panel.css';

type InheritedPage = NonNullable<
  GetDocInheritedAccessQuery['workspace']['doc']
>['inheritedAccess'];
type InheritedEntry = InheritedPage['edges'][number]['node'];

/** Read-only list of access a document receives through its folders. */
export function DocInheritedAccessPanel({
  onTotal,
}: {
  onTotal(total: number | null): void;
}) {
  const t = useI18n();
  const doc = useService(DocService).doc;
  const workspace = useService(WorkspaceService).workspace;
  const server = useService(WorkspaceServerService).server;
  const online = useObjectPermissionOnlineOnlyState({
    backendUnavailable: !server,
  });
  const [cursors, setCursors] = useState<Array<string | undefined>>([
    undefined,
  ]);
  const cursor = cursors.at(-1);
  const [snapshot, setSnapshot] = useState<{
    docId: string;
    cursor?: string;
    page: InheritedPage;
  }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const [folderId, setFolderId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setError(false);
    if (!server || online.offline) {
      setLoading(false);
      return () => controller.abort();
    }
    setLoading(true);
    server
      .gql({
        query: getDocInheritedAccessQuery,
        variables: {
          workspaceId: workspace.id,
          docId: doc.id,
          pagination: { first: PERMISSION_REVIEW_PAGE_SIZE, after: cursor },
        },
        context: {
          signal: AbortSignal.any([
            controller.signal,
            AbortSignal.timeout(15_000),
          ]),
        },
      })
      .then(result => {
        if (controller.signal.aborted) return;
        const inherited = result.workspace.doc?.inheritedAccess;
        if (!inherited) {
          setError(true);
          return;
        }
        setSnapshot({ docId: doc.id, cursor, page: inherited });
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [cursor, doc.id, online.offline, revision, server, workspace.id]);

  const page =
    snapshot?.docId === doc.id && snapshot.cursor === cursor
      ? snapshot.page
      : undefined;
  const total = page?.totalCount ?? null;
  useEffect(() => {
    if (total !== null) onTotal(total);
  }, [onTotal, total]);

  const rows = page?.edges.map(edge => edge.node) ?? [];
  const source = (entry: InheritedEntry) => {
    const name = entry.sourceName ?? '';
    const label = t['com.streamlain.share-menu.access.via-folder']({ name });
    const sourceId = entry.sourceId;
    return entry.sourceType === 'folder' && sourceId ? (
      <button
        type="button"
        className={panelStyles.sourceLink}
        onClick={() => setFolderId(sourceId)}
        data-testid={`doc-permission-inherited-source-${sourceId}`}
      >
        {label}
      </button>
    ) : (
      label
    );
  };
  const role = (entry: InheritedEntry) => (
    <span className={styles.roleLabel}>
      {objectPermissionRoleLabel(t, entry.effectiveRole)}
    </span>
  );

  return (
    <>
      <div className={reviewStyles.notice}>
        {t['com.streamlain.share-menu.access.inherited-note']()}
      </div>
      <PermissionReviewList
        testId="doc-permission-inherited"
        hideSearch
        search=""
        onSearchChange={() => {}}
        placeholder=""
        emptyMessage={t['com.streamlain.share-menu.access.inherited-empty']()}
        loading={loading}
        error={error}
        offline={online.offline}
        onRetry={() => setRevision(value => value + 1)}
        total={total}
        start={(cursors.length - 1) * PERMISSION_REVIEW_PAGE_SIZE}
        count={rows.length}
        onPrevious={
          cursors.length > 1
            ? () => setCursors(current => current.slice(0, -1))
            : undefined
        }
        onNext={
          page?.pageInfo.hasNextPage && page.pageInfo.endCursor
            ? () =>
                setCursors(current => [...current, page.pageInfo.endCursor!])
            : undefined
        }
      >
        {rows.map(entry => {
          const notice = getObjectPermissionAreaBlockedNotice(t, entry);
          const description =
            entry.memberCount === null || entry.memberCount === undefined ? (
              source(entry)
            ) : (
              <>
                {t['com.streamlain.share-menu.access.member-count']({
                  count: entry.memberCount,
                })}
                {' · '}
                {source(entry)}
              </>
            );
          return (
            <div
              key={`${entry.principalType}:${entry.principalId}:${entry.sourceId ?? ''}`}
              className={styles.accessRowFrame}
              data-area-blocked={entry.isAreaBlocked ? 'true' : undefined}
            >
              {entry.principalType === 'group' ? (
                <PermissionGroupRow
                  groupName={entry.displayName}
                  description={description}
                  roleSlot={role(entry)}
                  dataTestId={`doc-permission-inherited-group-${entry.principalId}`}
                />
              ) : (
                <PermissionMemberRow
                  user={{
                    id: entry.principalId,
                    name: entry.displayName,
                    email: entry.email ?? undefined,
                    avatarUrl: entry.avatarUrl,
                  }}
                  subtitle={description}
                  roleSelector={role(entry)}
                  dataTestId={`doc-permission-inherited-user-${entry.principalId}`}
                />
              )}
              {notice ? (
                <div className={styles.areaBlockedNotice}>{notice}</div>
              ) : null}
            </div>
          );
        })}
      </PermissionReviewList>
      {folderId ? (
        <FolderShareModal
          folderId={folderId}
          open
          onOpenChange={open => {
            if (!open) setFolderId(null);
          }}
        />
      ) : null}
    </>
  );
}
